import D from "@/admin/dashboard/Dashboard.module.css";

import { invoiceRevenueQueryAtom } from "@/admin/accounts/accountsQuery.ts";
import { CustomRadios, type FilterItem } from "@/admin/dashboard/CustomRadios.tsx";
import { HeadingBlock } from "@/admin/dashboard/HeadingBlock.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { PercentageChange } from "@/admin/dashboard/PercentageChange.tsx";
import { abbreviateNumber } from "@/admin/dashboard/utils.ts";
import { monthNames, WBarChart, type WBarChartOptions } from "@/admin/dashboard/WBarChart.tsx";
import { useTooltipPortal } from "@/admin/utils/useTooltipPortal.tsx";
import { BadgeDot } from "@/components/Badge.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useThemeVar } from "@/modules/theme/useTheme.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { BarSeriesOption } from "echarts/charts";
import { useState } from "react";
import { Label } from "react-aria-components";

type FilterBy = "excluded" | "included";
const filterItems: FilterItem<FilterBy>[] = [
    { key: "excluded", title: "Excluded" },
    { key: "included", title: "Included" },
];

function InvoicedRevenueSectionContent() {
    const limitMonths = 12;

    const data = useSuspenseQueryAtom(invoiceRevenueQueryAtom(limitMonths));
    const [filterBy, setFilterBy] = useState<FilterBy>("excluded");

    const colorWhite = useThemeVar("color-white");
    const colorMuted = useThemeVar("color-muted");
    const colorTextHoverAlpha = useThemeVar("color-text-hover-alpha");
    const colorText = useThemeVar("color-text");
    const colorChart1 = useThemeVar("color-primary");
    const colorChart2 = useThemeVar("color-chart-free");

    const xAxisData: string[] = [];

    for (let iteratorMonths = limitMonths; iteratorMonths > 0; iteratorMonths--) {
        const currentDate = new Date();
        const generatedDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - (iteratorMonths - 1), 1);
        const month = monthNames[generatedDate.getMonth()];
        const year = generatedDate.getFullYear();
        xAxisData.push(`${month} ${year}`);
    }

    let invoiceTotalLastMonth = 0;
    let invoiceTotalPrevMonth = 0;

    const filteredData = filterBy === "included" ? data : data.filter((item) => item.document_type !== "credit_note");

    const payablesData: number[] = [];
    const vatData: number[] = [];

    // biome-ignore lint/complexity/noForEach: XXX: Bad code! Should use `useMemo` or `.map()` instead of looping in render function.
    xAxisData.forEach((item) => {
        // Parse the "Month Year" string properly
        const [month, year] = item.split(" ");
        const monthIndex = monthNames.indexOf(month);
        const date = new Date(Number(year), monthIndex, 1); // Use 1st day of month

        const time = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()).valueOf() / 1000;
        const data = filteredData.filter((item) => {
            return item.period_start_month === time;
        });

        if (data.length < 2) {
            payablesData.push(data?.[0]?.total_before_tax ?? 0);
            vatData.push(data?.[0]?.vat_tax ?? 0);
        } else {
            payablesData.push(
                data[0].document_type === "credit_note"
                    ? data[1].total_before_tax - data[0].total_before_tax
                    : data[0].total_before_tax - data[1].total_before_tax,
            );
            vatData.push(
                data[0].document_type === "credit_note"
                    ? data[1].vat_tax - data[0].vat_tax
                    : data[0].vat_tax - data[1].vat_tax,
            );
        }
    });

    payablesData.forEach((item, index) => {
        const indexPrev = 3;
        const indexLast = 2;

        if (index + indexPrev === payablesData.length) {
            invoiceTotalPrevMonth = item;
        }

        if (index + indexLast === payablesData.length) {
            invoiceTotalLastMonth = item;
        }
    });

    const { portal, formatter } = useTooltipPortal(({ name, dataIndex }) => {
        return (
            <div>
                <div className="pb-1">
                    <b>{name}</b>
                </div>

                {!!payablesData[dataIndex] && (
                    <BadgeDot color="color-primary">
                        Payables (excl. VAT):{" "}
                        <b>
                            <CurrencyBalance value={payablesData[dataIndex]} />
                        </b>
                    </BadgeDot>
                )}

                {!!vatData[dataIndex] && (
                    <BadgeDot color="color-chart-free">
                        VAT:{" "}
                        <b>
                            <CurrencyBalance value={vatData[dataIndex]} />
                        </b>
                    </BadgeDot>
                )}
            </div>
        );
    });

    const payablesSeries: BarSeriesOption[] = [
        {
            name: "Payables (excl. VAT)",
            type: "bar",
            stack: "total",
            itemStyle: {
                color: colorChart1,
            },
            tooltip: { formatter },
            data: payablesData,
        },
    ];

    const vatSeries: BarSeriesOption[] = [
        {
            name: "VAT",
            type: "bar",
            stack: "total",
            itemStyle: {
                color: colorChart2,
            },
            tooltip: { formatter },
            data: vatData,
        },
    ];

    const options: WBarChartOptions = {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            borderWidth: 0,
            backgroundColor: colorWhite,
            textStyle: {
                color: colorText,
            },
            extraCssText: "box-shadow: 0 8px 20px rgba(0 0 0 / 10%);",
        },
        grid: {
            top: "5px",
            left: "5px",
            right: "5px",
            bottom: "5px",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: xAxisData,
            axisLine: {
                lineStyle: {
                    color: colorMuted,
                },
            },
            axisTick: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: (value) => abbreviateNumber(value),
            },
            splitLine: {
                lineStyle: {
                    color: colorTextHoverAlpha,
                },
            },
        },
        series: [...payablesSeries, ...vatSeries],
    };

    return (
        <>
            {portal}

            <div className={D.ItemHeader}>
                <div>
                    <h2 className="font-size-heading">Invoiced Revenue</h2>
                    <p>Revenue breakdown and actual payable amount</p>
                </div>

                <HeadingBlock
                    title="Last month payables (excl. VAT)"
                    subtitle={<CurrencyBalance value={invoiceTotalLastMonth} />}
                >
                    <PercentageChange last={invoiceTotalLastMonth} prev={invoiceTotalPrevMonth} />
                </HeadingBlock>
            </div>

            <Separator />

            <div className="flex justify-between pt-3 pb-3">
                <div className="flex gap-3 pt-3 pb-4 font-size-small">
                    <BadgeDot color="color-primary">Payables (excl. VAT)</BadgeDot>

                    <BadgeDot color="color-chart-free">VAT</BadgeDot>
                </div>

                <div className="flex items-center">
                    <Label className="font-size-small pr-1">Credit notes:</Label>
                    <div className="flex items-center">
                        <CustomRadios items={filterItems} value={filterBy} onChange={setFilterBy} />
                    </div>
                </div>
            </div>

            <WBarChart options={options} />
        </>
    );
}

export function InvoicedRevenueSection() {
    return (
        <LightBlock loadingHeight="450px">
            <InvoicedRevenueSectionContent />
        </LightBlock>
    );
}
